/**
 * Display-layer formatters for time and currency. Locale comes from i18next
 * (`es` or `en`); if a caller passes neither, we default to `en`.
 */

type Locale = 'es' | 'en' | string;

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Compact relative time ("just now", "5m", "2h", "3d") for table cells where
 * absolute timestamps would dominate. For dates older than a week we fall back
 * to an absolute date.
 */
export function formatRelativeTime(input: string | Date, locale: Locale = 'en', now: Date = new Date()): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = now.getTime() - date.getTime();
  const isEs = locale.startsWith('es');

  if (diffMs < 0) {
    // Future timestamp — odd but possible due to clock skew. Treat as "now".
    return isEs ? 'ahora' : 'just now';
  }
  if (diffMs < MINUTE) {
    return isEs ? 'ahora' : 'just now';
  }
  if (diffMs < HOUR) {
    const m = Math.floor(diffMs / MINUTE);
    return `${m}m`;
  }
  if (diffMs < DAY) {
    const h = Math.floor(diffMs / HOUR);
    return `${h}h`;
  }
  if (diffMs < 7 * DAY) {
    const d = Math.floor(diffMs / DAY);
    return `${d}d`;
  }
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
}

/**
 * Absolute date + time for table cells where the operator needs to see
 * exactly when something happened (e.g. call timestamps for debugging).
 */
export function formatDateTime(input: string | Date, locale: Locale = 'en'): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatCurrency(amount: number, locale: Locale = 'en', currency = 'USD'): string {
  // Per-unit prices like $0.0192/SMS are common in this product, so we widen
  // to 4 decimals when the amount is small but non-zero. An exact 0 is most
  // often an empty-state aggregate (no spend / no balance) and reads worst
  // with $0.0000 — keep the canonical $0.00 there.
  const minDigits = amount !== 0 && Math.abs(amount) < 1 ? 4 : 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minDigits,
    maximumFractionDigits: 4,
  }).format(amount);
}

/**
 * USD currency with a fixed 2 decimals — for rolled-up money totals (balances,
 * per-campaign net spend) where formatCurrency's up-to-4-decimals precision
 * (meant for per-unit prices like $0.0192/SMS) reads as noise. Kept as a
 * separate helper so formatCurrency's rate-friendly behavior stays untouched.
 */
export function formatCurrencyFixed2(amount: number, locale: Locale = 'en', currency = 'USD'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function truncate(text: string, max = 80): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + '…';
}
